
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreditCard, Coins, Check, Package, Loader2, User, Mail, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CreditTransaction } from '@/types/subscription';
import Header from '@/components/Header';
import { Table, TableBody, TableCell, TableHeader, TableHead, TableRow } from '@/components/ui/table';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { useAccountTabs, AccountTab } from '@/hooks/useAccountTabs';
import { ScrollArea } from '@/components/ui/scroll-area';

const Account: React.FC = () => {
  const { activeTab, handleTabChange } = useAccountTabs();
  const [searchParams] = useSearchParams();
  const { user, profile, credits, subscription, subscriptionPlan, hasActiveSubscription, refreshSubscription, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [companyName, setCompanyName] = useState(profile?.company_name || '');
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'credits') {
      toast({
        title: 'Transaktion erfolgreich',
        description: 'Ihre Credits wurden Ihrem Konto gutgeschrieben.',
        variant: 'default'
      });
      refreshSubscription();
    }
  }, [searchParams, toast, refreshSubscription]);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('credit_transactions')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(10);
        
        if (error) {
          throw error;
        }
        
        setTransactions(data || []);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      }
    };
    
    fetchTransactions();
  }, [user]);

  const formatSubscriptionDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'dd. MMMM yyyy', { locale: de });
  };

  const formatTransactionDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'dd.MM.yyyy HH:mm', { locale: de });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Aktiv</Badge>;
      case 'canceled':
        return <Badge variant="outline">Gekündigt</Badge>;
      case 'past_due':
        return <Badge variant="destructive">Zahlungsrückstand</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-customer-portal', {});
      
      if (error) {
        throw error;
      }
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating customer portal:', error);
      toast({
        title: 'Fehler',
        description: 'Kunde Portal konnte nicht geöffnet werden.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTransactionBadge = (type: string) => {
    switch (type) {
      case 'add':
        return <Badge className="bg-green-500">Gutschrift</Badge>;
      case 'use':
        return <Badge variant="outline">Nutzung</Badge>;
      case 'refund':
        return <Badge className="bg-amber-500">Rückerstattung</Badge>;
      default:
        return <Badge variant="secondary">{type}</Badge>;
    }
  };

  const handleUpdateProfile = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          full_name: fullName,
          company_name: companyName
        })
        .eq('id', user.id);
        
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Profil aktualisiert',
        description: 'Ihre persönlichen Daten wurden erfolgreich aktualisiert.',
      });
      
      setIsEditingProfile(false);
      refreshProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Fehler',
        description: 'Profil konnte nicht aktualisiert werden.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black to-gray-900 text-white p-4 md:p-8">
      <Header />
      <ScrollArea className="w-full h-[calc(100vh-5rem)] pb-16">
        <div className="max-w-3xl mx-auto pt-16 pb-16">
          <div className="flex flex-col md:flex-row items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold mb-1">Mein Konto</h1>
              <p className="text-gray-400">Verwalten Sie Ihr Konto und Abonnement</p>
            </div>
            <div className="mt-4 md:mt-0">
              <Button variant="outline" onClick={() => navigate('/')} className="border-gray-700 text-white hover:bg-gray-800">
                Zurück zur Startseite
              </Button>
            </div>
          </div>
          
          {/* Account Overview Card */}
          <Card className="bg-gray-800/50 border-gray-700 text-white mb-6">
            <CardHeader className="pb-2">
              <CardTitle>Kontoübersicht</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-1/3">
                  <div className="flex flex-col items-center">
                    <Avatar className="h-16 w-16 mb-2">
                      {profile?.avatar_url ? (
                        <AvatarImage src={profile.avatar_url} alt={profile.full_name || user?.email || ""} />
                      ) : (
                        <AvatarFallback className="bg-jade text-lg">
                          {(profile?.full_name?.charAt(0) || user?.email?.charAt(0) || "").toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="text-center">
                      <p className="font-medium">{profile?.full_name || "Kein Name"}</p>
                      <p className="text-sm text-gray-400">{user?.email}</p>
                    </div>
                  </div>
                </div>
                
                <div className="md:w-2/3 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-700/30 p-4 rounded">
                      <div className="flex items-center mb-1">
                        <Coins className="h-4 w-4 text-jade mr-2" />
                        <span className="text-gray-300 text-sm">Credits</span>
                      </div>
                      <div className="text-xl font-bold">{credits}</div>
                    </div>
                    
                    <div className="bg-gray-700/30 p-4 rounded">
                      <div className="flex items-center mb-1">
                        <Package className="h-4 w-4 text-jade mr-2" />
                        <span className="text-gray-300 text-sm">Plan</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-lg font-medium mr-2">{subscriptionPlan ? subscriptionPlan.name : 'Free Trial'}</span>
                        {subscription && (
                          <div>{getStatusBadge(subscription.status)}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {subscription && subscription.status === 'active' && (
                    <div className="flex justify-end">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={handleManageSubscription}
                        disabled={isLoading}
                        className="border-gray-700"
                      >
                        {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                        Abonnement verwalten
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Tabs value={activeTab} onValueChange={(v) => handleTabChange(v as AccountTab)} className="mb-8">
            <TabsList className="bg-gray-800/50 border-gray-700 w-full mb-4">
              <TabsTrigger value="subscription" className="flex-1">Abonnement</TabsTrigger>
              <TabsTrigger value="personal" className="flex-1">Persönliche Daten</TabsTrigger>
              <TabsTrigger value="usage" className="flex-1">Nutzungsverlauf</TabsTrigger>
            </TabsList>
            
            <TabsContent value="subscription" className="h-auto overflow-visible">
              <Card className="bg-gray-800/20 border-gray-700 text-white">
                <CardHeader>
                  <CardTitle>Abonnement verwalten</CardTitle>
                  <CardDescription className="text-gray-400">
                    Verwalten Sie Ihr Abonnement und Credits
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {hasActiveSubscription ? (
                    <div className="space-y-4">
                      <div className="bg-gray-800/50 p-4 rounded-md">
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="font-medium">{subscriptionPlan?.name}</h3>
                            <p className="text-gray-400 text-sm mt-1">
                              {subscriptionPlan?.credits} Credits pro Abrechnungszeitraum
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-300 mb-1">Nächste Abrechnung</div>
                            <div className="font-medium">{formatSubscriptionDate(subscription?.current_period_end)}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <Button 
                          onClick={handleManageSubscription} 
                          disabled={isLoading}
                          className="bg-jade hover:bg-jade/90 text-white w-full"
                        >
                          {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <CreditCard className="h-4 w-4 mr-2" />
                          )}
                          Abonnement und Zahlungen verwalten
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-gray-800/50 p-4 rounded-md">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h3 className="font-medium">Free Trial</h3>
                            <p className="text-gray-400 text-sm mt-1">5 Credits pro Monat</p>
                          </div>
                          <Badge>Aktuell</Badge>
                        </div>
                        
                        <Separator className="my-4 bg-gray-700" />
                        
                        <p className="text-sm text-gray-300 mb-3">Upgrade für mehr Features:</p>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-center">
                            <Check className="h-4 w-4 text-jade mr-2" />
                            <span>Mehr Credits für E-Mails und Dokumente</span>
                          </li>
                          <li className="flex items-center">
                            <Check className="h-4 w-4 text-jade mr-2" />
                            <span>Prioritäts-Support</span>
                          </li>
                          <li className="flex items-center">
                            <Check className="h-4 w-4 text-jade mr-2" />
                            <span>Zugang zu neuen Features</span>
                          </li>
                        </ul>
                      </div>
                      
                      <Button 
                        onClick={() => navigate('/pricing')} 
                        className="w-full bg-jade hover:bg-jade/90"
                      >
                        <Coins className="h-4 w-4 mr-2" />
                        Preispläne anzeigen
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="personal" className="h-auto overflow-visible">
              <Card className="bg-gray-800/20 border-gray-700 text-white">
                <CardHeader>
                  <CardTitle>Persönliche Daten</CardTitle>
                  <CardDescription className="text-gray-400">
                    Verwalten Sie Ihre persönlichen Informationen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isEditingProfile ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Voller Name</label>
                        <Input 
                          value={fullName} 
                          onChange={(e) => setFullName(e.target.value)}
                          className="bg-gray-800/70 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Firmenname</label>
                        <Input 
                          value={companyName} 
                          onChange={(e) => setCompanyName(e.target.value)}
                          className="bg-gray-800/70 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                        <Input 
                          value={user?.email || ""}
                          readOnly
                          className="bg-gray-800/70 border-gray-600 text-white opacity-70"
                        />
                      </div>
                      <div className="flex gap-2 justify-end pt-2">
                        <Button variant="outline" onClick={() => setIsEditingProfile(false)} className="border-gray-700">
                          Abbrechen
                        </Button>
                        <Button onClick={handleUpdateProfile} className="bg-jade hover:bg-jade/90">
                          Speichern
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h3 className="text-sm font-medium text-gray-300 mb-1">Name</h3>
                          <div className="p-2.5 bg-gray-800/50 rounded">{profile?.full_name || "Nicht angegeben"}</div>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-300 mb-1">Email</h3>
                          <div className="p-2.5 bg-gray-800/50 rounded">{user?.email}</div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-sm font-medium text-gray-300 mb-1">Firma</h3>
                        <div className="p-2.5 bg-gray-800/50 rounded">{profile?.company_name || "Nicht angegeben"}</div>
                      </div>
                      
                      <div className="flex justify-end">
                        <Button onClick={() => setIsEditingProfile(true)} variant="outline" className="border-gray-700">
                          <User className="h-4 w-4 mr-2" />
                          Profil bearbeiten
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="usage" className="h-auto overflow-visible">
              <Card className="bg-gray-800/20 border-gray-700 text-white">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="mr-2 h-5 w-5 text-jade" />
                    Nutzungsverlauf
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Übersicht Ihrer Credit-Transaktionen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {transactions.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-gray-700 hover:bg-transparent">
                            <TableHead className="text-white">Datum</TableHead>
                            <TableHead className="text-white">Typ</TableHead>
                            <TableHead className="text-white">Beschreibung</TableHead>
                            <TableHead className="text-white text-right">Credits</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transactions.map((transaction) => (
                            <TableRow key={transaction.id} className="border-gray-700 hover:bg-gray-800/50">
                              <TableCell className="font-medium">{formatTransactionDate(transaction.created_at)}</TableCell>
                              <TableCell>{getTransactionBadge(transaction.transaction_type)}</TableCell>
                              <TableCell>{transaction.description || '-'}</TableCell>
                              <TableCell className={`text-right ${transaction.transaction_type === 'add' ? 'text-green-500' : transaction.transaction_type === 'use' ? 'text-red-500' : 'text-amber-500'}`}>
                                {transaction.transaction_type === 'add' ? '+' : '-'}{Math.abs(transaction.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-800/30 rounded-md">
                      <Mail className="h-12 w-12 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400">Keine Transaktionen vorhanden.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </ScrollArea>
    </div>
  );
};

export default Account;
